import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OtcStatus, OtcSide, otcHistoryTabType } from './types';

// Generic filter type for OTC history
export type OtcHistoryFilter<StatusType = string> = {
  asset?: string;
  status?: StatusType;
  side?: OtcSide;
  createdTimeStart?: string;
  createdTimeEnd?: string;
  filledTimeStart?: string;
  filledTimeEnd?: string;
};

// Define specific filter types for each OTC tab
export type OtcBuyFilter = OtcHistoryFilter<OtcStatus>;
export type OtcSellFilter = OtcHistoryFilter<OtcStatus>;
export type OtcExchangeFilter = OtcHistoryFilter<OtcStatus>;

// Define the state type with specific status types for each tab
type OtcHistoryStateType = {
  otcHistory: {
    buy: OtcBuyFilter;
    sell: OtcSellFilter;
    exchange: OtcExchangeFilter;
  };
};

// Type map to ensure type safety when setting status
export type OtcStatusTypeMap = {
  buy: OtcStatus;
  sell: OtcStatus;
  exchange: OtcStatus;
};

// Type map for OTC history filters
export type OtcHistoryFilterMap = {
  buy: OtcBuyFilter;
  sell: OtcSellFilter;
  exchange: OtcExchangeFilter;
};

const initialState: OtcHistoryStateType = {
  otcHistory: {
    buy: {
      asset: '',
      status: undefined,
      side: undefined,
      createdTimeStart: '',
      createdTimeEnd: '',
      filledTimeStart: '',
      filledTimeEnd: ''
    },
    sell: {
      asset: '',
      status: undefined,
      side: undefined,
      createdTimeStart: '',
      createdTimeEnd: '',
      filledTimeStart: '',
      filledTimeEnd: ''
    },
    exchange: {
      asset: '',
      status: undefined,
      side: undefined,
      createdTimeStart: '',
      createdTimeEnd: '',
      filledTimeStart: '',
      filledTimeEnd: ''
    }
  }
};

const otcHistorySlice = createSlice({
  name: 'otcHistory',
  initialState,
  reducers: {
    setOtcAssetFilter: (
      state,
      action: PayloadAction<{ tab: otcHistoryTabType; asset: string }>
    ) => {
      const { tab, asset } = action.payload;
      if (state.otcHistory[tab]) {
        state.otcHistory[tab].asset = asset;
      }
    },
    setOtcSideFilter: (
      state,
      action: PayloadAction<{ tab: otcHistoryTabType; side: OtcSide }>
    ) => {
      const { tab, side } = action.payload;
      if (state.otcHistory[tab]) {
        state.otcHistory[tab].side = side;
      }
    },
    setOtcDateFilters: (
      state,
      action: PayloadAction<{
        tab: otcHistoryTabType;
        createdTimeStart?: string;
        createdTimeEnd?: string;
        filledTimeStart?: string;
        filledTimeEnd?: string;
      }>
    ) => {
      const {
        tab,
        createdTimeStart,
        createdTimeEnd,
        filledTimeStart,
        filledTimeEnd
      } = action.payload;
      if (state.otcHistory[tab]) {
        if (createdTimeStart !== undefined)
          state.otcHistory[tab].createdTimeStart = createdTimeStart;
        if (createdTimeEnd !== undefined)
          state.otcHistory[tab].createdTimeEnd = createdTimeEnd;
        if (filledTimeStart !== undefined)
          state.otcHistory[tab].filledTimeStart = filledTimeStart;
        if (filledTimeEnd !== undefined)
          state.otcHistory[tab].filledTimeEnd = filledTimeEnd;
      }
    },
    setOtcStatusFilter: {
      reducer: (
        state,
        action: PayloadAction<{
          tab: otcHistoryTabType;
          status: any;
        }>
      ) => {
        const { tab, status } = action.payload;
        if (state.otcHistory[tab]) {
          switch (tab) {
            case 'buy':
              (state.otcHistory.buy.status as any) = status;
              break;
            case 'sell':
              (state.otcHistory.sell.status as any) = status;
              break;
            case 'exchange':
              (state.otcHistory.exchange.status as any) = status;
              break;
            default:
              break;
          }
        }
      },
      prepare: <T extends otcHistoryTabType>({
        tab,
        status
      }: {
        tab: T;
        status: OtcStatusTypeMap[T];
      }) => {
        return { payload: { tab, status } };
      }
    },
    setOtcAllFilters: {
      reducer: (
        state,
        action: PayloadAction<{
          tab: otcHistoryTabType;
          filters: any;
        }>
      ) => {
        const { tab, filters } = action.payload;
        switch (tab) {
          case 'buy':
            state.otcHistory.buy = {
              ...state.otcHistory.buy,
              ...filters
            };
            break;
          case 'sell':
            state.otcHistory.sell = {
              ...state.otcHistory.sell,
              ...filters
            };
            break;
          case 'exchange':
            state.otcHistory.exchange = {
              ...state.otcHistory.exchange,
              ...filters
            };
            break;
          default:
            break;
        }
      },
      prepare: <T extends otcHistoryTabType>({
        tab,
        filters
      }: {
        tab: T;
        filters: Partial<OtcHistoryFilterMap[T]>;
      }) => {
        return { payload: { tab, filters } };
      }
    },
    clearOtcHistoryFilters: (
      state,
      action: PayloadAction<otcHistoryTabType>
    ) => {
      const tab = action.payload;
      switch (tab) {
        case 'buy':
          state.otcHistory.buy = initialState.otcHistory.buy;
          break;
        case 'sell':
          state.otcHistory.sell = initialState.otcHistory.sell;
          break;
        case 'exchange':
          state.otcHistory.exchange = initialState.otcHistory.exchange;
          break;
        default:
          break;
      }
    },
    clearAllOtcFilters: (state) => {
      state.otcHistory = initialState.otcHistory;
    }
  }
});

export const {
  setOtcAssetFilter,
  setOtcSideFilter,
  setOtcDateFilters,
  setOtcStatusFilter,
  setOtcAllFilters,
  clearOtcHistoryFilters,
  clearAllOtcFilters
} = otcHistorySlice.actions;

export default otcHistorySlice.reducer;
